import React, {useContext} from "react";
import PredictionsList from "./PredictionsList";
import {AppContext} from "../index";

function keyDown(
  key: string,
  setIndex: React.Dispatch<React.SetStateAction<number>>,
  index: number,
  predictions: google.maps.places.AutocompletePrediction[],
  setValue: (val: string) => void,
  setPredictions: React.Dispatch<React.SetStateAction<google.maps.places.AutocompletePrediction[]>>,
  button: React.RefObject<HTMLButtonElement>
) {
  switch (key) {
    case "ArrowDown":
      setIndex((old) => {
        if (old < 4) {
          return old + 1;
        } else {
          return 4;
        }
      });
      break;
    case "ArrowUp":
      setIndex((old) => {
        if (old > -1) {
          return old - 1;
        } else {
          return -1;
        }
      });
      break;
    case "Enter":
    case "NumpadEnter":
      if (predictions[index]) {
        setValue(predictions[index].description);
        setIndex(-1);
      }
      button.current?.click();
      break;
    default:
      setIndex(-1);
      break;
  }
}

export default function AutoCompleteWrapper({
  autoCompleteInput,
  setInput,
  setPredictions,
  autocomplete,
  setIndex,
  index,
  predictions,
  submitButton,
  submitSearch,
  map,
  setMarkedEntry,
  setMapCenter,
  latLngBounds,
  panAndZoomToCoordinates,
}: {
  autoCompleteInput: React.RefObject<HTMLInputElement>;
  setInput: (val: string) => void;
  setPredictions: (
    value:
      | ((prevState: google.maps.places.AutocompletePrediction[]) => google.maps.places.AutocompletePrediction[])
      | google.maps.places.AutocompletePrediction[]
  ) => void;
  autocomplete: google.maps.places.AutocompleteService | null;
  setIndex: (value: ((prevState: number) => number) | number) => void;
  index: number;
  predictions: google.maps.places.AutocompletePrediction[];
  submitButton: React.RefObject<HTMLButtonElement>;
  submitSearch: () => void;
  map: google.maps.Map | null;
  setMarkedEntry: (value: ((prevState: number) => number) | number) => void;
  setMapCenter: (
    value: ((prevState: { lng: number; lat: number }) => { lng: number; lat: number }) | { lng: number; lat: number }
  ) => void;
  latLngBounds: google.maps.LatLngBoundsLiteral;
  panAndZoomToCoordinates: (
    map: google.maps.Map,
    coordinates: google.maps.LatLngLiteral,
    placeMarker?: boolean
  ) => Promise<void>;
}) {
  const autoCompleteOptions: google.maps.places.AutocompleteOptions = {
    types: ["locality", "postal_code", "administrative_area_level_3"],
    componentRestrictions: { country: ["DE", "AT", "CH", "IT", "FR", "ES"] },
    bounds: latLngBounds,
    strictBounds: true,
    fields: ["description", "formatted_address", "types"],
  };

  const { textStrings } = useContext(AppContext);

  function inputChange(
    event: React.ChangeEvent<HTMLInputElement>,
    setInput: (val: string) => void,
    setPredictions: React.Dispatch<React.SetStateAction<google.maps.places.AutocompletePrediction[]>>,
    autoComplete: google.maps.places.AutocompleteService | null
  ) {
    const val = event.target.value;
    setInput(val);
    if (val && autoComplete) {
      autoComplete
        .getPlacePredictions({
          ...autoCompleteOptions,
          input: val,
        })
        .then(({ predictions }) => setPredictions(predictions));
    } else {
      setPredictions([]);
    }
  }

  return (
    <>
      <div className="autocomplete-wrapper">
        <input
          type="text"
          placeholder={textStrings.searchBarPlaceholder}
          className="autocomplete-input"
          id="map__autocomplete"
          ref={autoCompleteInput}
          onChange={(e) => inputChange(e, setInput, setPredictions, autocomplete)}
          autoFocus={true}
          onKeyDown={(e) => keyDown(e.code, setIndex, index, predictions, setInput, setPredictions, submitButton)}
        />
        <button
          type="submit"
          id="search-button"
          onClick={submitSearch}
          ref={submitButton}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="currentColor"
            className="bi bi-search"
            viewBox="0 0 16 16">
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
          </svg>
        </button>
        {map && navigator.geolocation && (
          <button
            type="button"
            id="locate_me_button"
            onClick={() =>
              navigator.geolocation.getCurrentPosition((res) => {
                const coords = { lat: res.coords.latitude, lng: res.coords.longitude };
                panAndZoomToCoordinates(map, coords, true);
                setMarkedEntry(-1);
                setMapCenter(coords);
              })
            }>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="currentColor"
              className="bi bi-bullseye"
              viewBox="0 0 16 16">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              <path d="M8 13A5 5 0 1 1 8 3a5 5 0 0 1 0 10zm0 1A6 6 0 1 0 8 2a6 6 0 0 0 0 12z" />
              <path d="M8 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0 1a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
              <path d="M9.5 8a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
            </svg>
          </button>
        )}
      </div>
      <PredictionsList
        setValue={setInput}
        predictions={predictions}
        setPredictions={setPredictions}
        index={index}
        button={submitButton}
      />
    </>
  );
}
